/**
 * The shared contract of the full-song videos (visualizer, lyric video, music video) and of the
 * lyric moment (spec `2026-10-05-music24-full-videos-and-lyric-sync-design.md` §4, §6, §7): the
 * length and trim rules, the render parts, the progress and time estimate, the song map, the plan
 * of the director, and the lyric and title fields of the timeline.
 *
 * The director (`music24-api/src/kit/director`, `src/kit/plan`) makes these values, the render
 * pipeline (`RenderKitAssetJob`, the part and join jobs) and the renderer (`src/video`) read them,
 * and the web app shows the estimate. The zod schema of `FullVideoPlan`
 * (`music24-api/src/kit/plan/fullVideoPlanSchema.ts`) must give exactly this type.
 *
 * The file imports only types, so every project can use it. Every function is pure.
 */
import type { WideArtworkLayout } from './assetStyles';
import type { ThemeCaptionStyle, ThemeSegmentMotion, ThemeTransition } from './releaseThemes';
import type { StudioThemeId } from './studioThemes';

/** A full video is at least 30 s long (also its trim). */
export const FULL_VIDEO_MIN_SEC = 30;
/** A full video is at most 900 s long: music24-media refuses a longer song (`tooLong`). */
export const FULL_VIDEO_MAX_SEC = 900;
/** One render part is 900 frames (30 s at 30 fps); the last part is shorter (spec §6.2). */
export const FULL_VIDEO_PART_FRAMES = 900;
/** The most shots in the plan of a music video. */
export const FULL_VIDEO_MAX_SHOTS = 120;
/** The most sections of a song map that is cut at energy changes (a song with no lyric timing). */
export const SONG_MAP_MAX_ENERGY_SECTIONS = 12;
/** The same clip and window never shows twice inside this time (spec §7.3). */
export const CLIP_WINDOW_REPEAT_MIN_GAP_SEC = 20;
/** Below this count of published clips that match the theme moods, the director widens the moods. */
export const MUSIC_VIDEO_MIN_MATCHING_CLIPS = 12;
/** The first shot of a music video holds the title this long (seconds, min and max). */
export const FULL_VIDEO_TITLE_SEC: readonly [number, number] = [2, 4];
/** The end card (cover card, "artist · title", the logo) of a music video, in seconds. */
export const FULL_VIDEO_END_CARD_SEC = 3;

/** The optional trim of a full video (`params.trim`), in song seconds, snapped to downbeats. */
export interface KitAssetTrim {
  startSec: number;
  endSec: number;
}

/**
 * True when the trim fits a song of `durationSec`: finite, 0 ≤ start < end ≤ song, and at least
 * `FULL_VIDEO_MIN_SEC` long. Else create answers 400 `M24_TRIM_INVALID`. The snap to downbeats comes
 * after this check.
 */
export function isValidTrim(trim: KitAssetTrim, durationSec: number): boolean {
  const { startSec, endSec } = trim;
  return (
    Number.isFinite(startSec) &&
    Number.isFinite(endSec) &&
    startSec >= 0 &&
    endSec <= durationSec &&
    endSec - startSec >= FULL_VIDEO_MIN_SEC
  );
}

/** The length of a full video: the trim, else the whole song, at most `FULL_VIDEO_MAX_SEC`. */
export function fullVideoLengthSec(durationSec: number, trim?: KitAssetTrim | null): number {
  const length = trim === undefined || trim === null ? durationSec : trim.endSec - trim.startSec;
  return Math.min(Math.max(0, length), FULL_VIDEO_MAX_SEC);
}

/** One render part: the frames `fromFrame` to `toFrame`, both included (the Remotion `frameRange`). */
export interface FullVideoPart {
  index: number;
  fromFrame: number;
  toFrame: number;
}

/** The render parts of a video of `durationFrames` frames, in order. */
export function fullVideoParts(durationFrames: number): FullVideoPart[] {
  const parts: FullVideoPart[] = [];
  for (let fromFrame = 0; fromFrame < durationFrames; fromFrame += FULL_VIDEO_PART_FRAMES) {
    parts.push({
      index: parts.length,
      fromFrame,
      toFrame: Math.min(fromFrame + FULL_VIDEO_PART_FRAMES, durationFrames) - 1,
    });
  }
  return parts;
}

/**
 * The `progress` (0..100, whole numbers) of a full video (spec §6.6): the parts take 95 %, the join
 * the last 5 %. `currentPart` is the Remotion progress (0..1) of the part that renders now.
 */
export function fullVideoProgress(input: {
  partsDone: number;
  partsTotal: number;
  currentPart: number;
  joined: boolean;
}): number {
  if (input.joined) {
    return 100;
  }
  if (input.partsTotal <= 0) {
    return 0;
  }
  const current = input.partsDone >= input.partsTotal ? 0 : Math.min(Math.max(input.currentPart, 0), 1);
  const share = Math.min((input.partsDone + current) / input.partsTotal, 1);
  return Math.floor(share * 95);
}

/**
 * The part that renders now, counted from 1, for "Rendering part 4 of 8" (design s10). The server
 * field `renderPartsDone` counts the parts that are FINISHED, so the part in progress is the next
 * one; while the join runs (every part done) it stays at the last part. Null with no parts. Pure.
 */
export function renderPartInProgress(
  partsDone: number | null | undefined,
  partsTotal: number | null | undefined
): number | null {
  if (partsTotal === null || partsTotal === undefined || partsTotal <= 0) {
    return null;
  }
  const done = Math.max(0, Math.floor(partsDone ?? 0));
  return Math.min(done + 1, partsTotal);
}

/** The render seconds per song second of the estimate before the first measurement (spec §6.5). */
export const FULL_VIDEO_ETA_SEC_PER_SONG_SEC: Readonly<Partial<Record<StudioThemeId, number>>> = {
  visualizer: 4,
  lyricVideo: 4,
  musicVideo: 6,
};

/**
 * The time estimate of a full video that the Studio shows before "Make it" (spec §6.5): the
 * render seconds per song second (`secPerSongSec`, else the table) × the length, plus 60 s, rounded
 * up to whole 5 minutes. Seconds; null for an asset type with no rate.
 */
export function fullVideoEtaSec(themeId: StudioThemeId, lengthSec: number, secPerSongSec?: number): number | null {
  const rate = secPerSongSec ?? FULL_VIDEO_ETA_SEC_PER_SONG_SEC[themeId];
  if (rate === undefined || !Number.isFinite(lengthSec) || lengthSec <= 0) {
    return null;
  }
  return Math.ceil((lengthSec * rate + 60) / 300) * 300;
}

// ---------------------------------------------------------------------------------------------
// The song map (code, no LLM; spec §7.1)
// ---------------------------------------------------------------------------------------------

/**
 * The kind of a section. `verse`: a lyric block that repeats no other block. `chorus`: a lyric
 * block that repeats another block. `section`: a part of a song with no lyric timing, cut at an
 * energy change.
 */
export const SONG_SECTION_KINDS = ['intro', 'verse', 'chorus', 'instrumental', 'outro', 'section'] as const;
export type SongSectionKind = (typeof SONG_SECTION_KINDS)[number];

export interface SongMapSection {
  index: number;
  kind: SongSectionKind;
  /** For the prompt: `intro`, `verse 1`, `chorus`, `chorus 2`, `section 3`, ... */
  label: string;
  startSec: number;
  endSec: number;
  /** The mean energy, 1 (low) to 5 (high). */
  energy: 1 | 2 | 3 | 4 | 5;
  beatCount: number;
  downbeatCount: number;
  /** The `lyricTiming` line indexes in the section; empty with no timing. */
  lineIndexes: number[];
  /** For a chorus after the first: the index of the first chorus whose shots it repeats. */
  repeatsSection: number | null;
}

export interface SongMap {
  /** The length of the video: the trim or the whole song. Section times are song seconds. */
  startSec: number;
  endSec: number;
  /** True when the sections come from the lyric timing, false when they come from the energy. */
  fromLyrics: boolean;
  sections: SongMapSection[];
}

// ---------------------------------------------------------------------------------------------
// The plan of the director (one Gemini call; spec §7.2)
// ---------------------------------------------------------------------------------------------

export const FULL_VIDEO_EMPHASES = ['low', 'mid', 'high'] as const;
export type FullVideoEmphasis = (typeof FULL_VIDEO_EMPHASES)[number];

/** One shot of a music video section. `clipId` is a published `BankClipModel.id`. */
export interface FullVideoShot {
  clipId: number;
  motion: ThemeSegmentMotion;
  transitionIn: ThemeTransition;
}

export interface FullVideoPlanSection {
  /** The `SongMapSection.index`. The plan has one entry per section, in order. */
  index: number;
  motion: ThemeSegmentMotion;
  /** The transition into the section. */
  transition: ThemeTransition;
  emphasis: FullVideoEmphasis;
  /** Music video only: the shots in order. The visualizer and the lyric video have none. */
  shots?: FullVideoShot[];
}

/**
 * The plan of a full video. The director never writes a lyric, a title text or an end card text:
 * code builds every text layer (plan rule 18). `endCard` is always true for a music video.
 */
export interface FullVideoPlan {
  schemaVersion: 1;
  sections: FullVideoPlanSection[];
  /** When the title shows, in video seconds. */
  title: { atSec: number };
  endCard: boolean;
  socialCaption: string;
  rationale: string;
}

// ---------------------------------------------------------------------------------------------
// The timeline fields of the lyric and title layers (renderer parts R-L2 to R-L4)
// ---------------------------------------------------------------------------------------------

/** One word of a lyric layer, in video frames. Every word has a time (`wordTimesOf`). */
export interface KitLyricWord {
  text: string;
  fromFrame: number;
  /** Excluded: the word is lit up to `toFrame - 1`. */
  toFrame: number;
}

/**
 * One sung line on screen, built by code from the lyric timing (rule 18): the lyric video, the lyric
 * moment and the captions of the music video. The renderer draws every line in the display font of
 * the theme in one column that moves (design u, `lyricLayout`): the line arrives in the middle at
 * `fromFrame` and stays at full alpha up to `sungToFrame`. The column reads the line before and the
 * next line from the list of layers.
 */
export interface KitLyricLayer {
  /** The `lyricTiming` line index. */
  lineIndex: number;
  text: string;
  /** The sung start of the line. */
  fromFrame: number;
  /** The time on screen (`lineScreenSpan`): to the next line, or to the sung end before a long gap. */
  durationFrames: number;
  /** The sung end of the line (excluded), inside `fromFrame + 1 .. fromFrame + durationFrames`. */
  sungToFrame: number;
  words: KitLyricWord[];
  /** `line`: the line shows; `word`: each word lights up at its time; `kinetic`: each word moves in. */
  captionStyle: ThemeCaptionStyle;
  /**
   * A couplet of the 16:9 lyric video (`lyricCouplets`, design y): two dense lines in one item; the
   * second line starts at this word and keeps its own row. Absent: one line.
   */
  coupletBreak?: number;
}

/**
 * How the lyric lines sit (design u): `column` the lyric video before design y (the line before, the
 * sung line, the next line; the title first and the rest items of the gaps), `moment` the 9:16
 * lyric moment (the words light up in every theme), `caption` the music video (one line at the
 * bottom: no line before, no next line), `wide` the lyric video of design y (the column on the right of the 16:9
 * stage; the stage shows the title card, the rests and the end card, so the column has no title,
 * rest or end item).
 */
export const KIT_LYRIC_LAYOUTS = ['column', 'moment', 'caption', 'wide'] as const;
export type KitLyricLayout = (typeof KIT_LYRIC_LAYOUTS)[number];

/** The title of the song in the lyric column: the first item, the rest items of long gaps and the end item. */
export interface KitLyricTitle {
  title: string;
  artist: string;
}

/**
 * The title layer with two states (R-L4): large with the text animation of the theme from
 * `large.fromFrame`, then small from `small.fromFrame` (after the first chorus) to the end. A video
 * with no small state has `small: null`.
 */
export interface KitTitleStates {
  large: { fromFrame: number; toFrame: number };
  small: { fromFrame: number; toFrame: number } | null;
}

/** The end card of a music video: the cover card and "artist · title", and the logo when the kit has one. */
export interface KitEndCard {
  fromFrame: number;
  durationFrames: number;
  text: string;
  withLogo: boolean;
}

/**
 * The fields that a full video or a lyric moment adds to the kit timeline. All optional: a short
 * asset has none of them. `wideArtworkLayout` comes from `wideArtworkLayoutOf(style, waveform)` on
 * a 16:9 frame.
 */
export interface KitFullVideoTimelineFields {
  wideArtworkLayout?: WideArtworkLayout | null;
  lyricLayers?: KitLyricLayer[];
  /** How the lyric lines sit. Absent (a plan from before the field): `moment` on 9:16, else `column`. */
  lyricLayout?: KitLyricLayout | null;
  /** The song title of the lyric column (the lyric video); null for the moment and the captions. */
  lyricTitle?: KitLyricTitle | null;
  titleStates?: KitTitleStates | null;
  endCard?: KitEndCard | null;
}
